import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import { cn } from "../lib/utils";
const DefaultLink = ({ href, className, children, target, }) => (_jsx("a", { href: href, className: className, target: target, rel: target === "_blank" ? "noopener noreferrer" : undefined, children: children }));
const noImage = () => null;
const BlockRenderContext = React.createContext({
    resolveImage: noImage,
    Link: DefaultLink,
    placeholders: "none",
});
export function useBlockRender() {
    return React.useContext(BlockRenderContext);
}
/**
 * Supplies image resolution + link rendering to every block underneath. Both
 * surfaces (CMS canvas, public site) render through the same components; only
 * this provider differs, which is what keeps the preview 1:1 with production.
 */
export function BlockRenderProvider({ resolveImage, Link, onContactFormSubmitted, submitContactInquiry, contactProtection, submitMailingListSignup, placeholders, children, }) {
    const value = React.useMemo(() => ({
        resolveImage: resolveImage ?? noImage,
        Link: Link ?? DefaultLink,
        placeholders: placeholders ?? "none",
        onContactFormSubmitted,
        submitContactInquiry,
        contactProtection,
        submitMailingListSignup,
    }), [
        resolveImage,
        Link,
        onContactFormSubmitted,
        submitContactInquiry,
        contactProtection,
        submitMailingListSignup,
        placeholders,
    ]);
    return (_jsx(BlockRenderContext.Provider, { value: value, children: children }));
}
/** A link that renders via the injected {@link BlockLinkComponent}. */
export function BlockLink({ href, className, children, target, }) {
    const { Link } = useBlockRender();
    return (_jsx(Link, { href: href, className: className, target: target, children: children }));
}
const IMAGE_CLASS = "h-auto w-full rounded-lg object-cover";
/**
 * Which framing a screen gets, expressed as CSS so the browser decides — and
 * keeps deciding.
 *
 * Every framing is rendered; exactly one is displayed. The step widths are the
 * container breakpoints blocks already use (`@min-[640px]:` and up), and these
 * are CONTAINER queries, not viewport ones: a block is wrapped in `@container`
 * (see BlockRenderer), so it responds to its own width. That is also why this
 * cannot be a `<picture>` — no media query can ask about a container.
 *
 * Written as whole literal strings because Tailwind scans source text for class
 * names; composing them from the step widths at runtime would emit no CSS.
 * `inline` rather than `block` is the img default, so a displayed framing lays
 * out exactly as the single-target `<img>` below does.
 *
 * Indexed by how many framings there are — tallest first (see MediaSlotView).
 * A slot with more framings than there are steps falls back to the single
 * primary framing rather than showing several at once.
 *
 * The two-step case is named, because a block art-directed PER SCREEN needs the
 * same two literals for a different mechanism: there, each screen has its own
 * single-target slot and its own stored picture, and the same container step
 * decides which of the two is displayed. One definition, so the breakpoint the
 * two mechanisms agree on cannot drift apart.
 */
export const NARROW_SCREEN_ONLY = "@min-[640px]:hidden";
export const WIDE_SCREEN_ONLY = "hidden @min-[640px]:inline";
const FRAMING_VISIBILITY = {
    2: [NARROW_SCREEN_ONLY, WIDE_SCREEN_ONLY],
    3: [
        "@min-[640px]:hidden",
        "hidden @min-[640px]:inline @min-[1024px]:hidden",
        "hidden @min-[1024px]:inline",
    ],
};
/**
 * A decoration clip: silent, looping, and STILL for anyone who asked for less
 * motion.
 *
 * `autoPlay` is deliberately absent from the markup. The attribute cannot be
 * withdrawn per visitor — it ships in the server-rendered HTML, and the
 * `prefers-reduced-motion` reset in @diality/ui's base layer reaches CSS
 * animations only, never a playing video — so a clip that autoplays for anyone
 * autoplays for everyone. Starting from an effect inverts that: stillness is
 * what the page renders, and motion is added only once the visitor's own
 * setting says it is welcome. `MediaPlayback`'s promise, kept.
 *
 * Consequences worth stating, both intended. A visitor who changes the setting
 * is honoured without reloading, because the query is listened to rather than
 * read once. And with JavaScript off nothing plays: the poster stands in, which
 * is the right resting state for something the page does not need in order to
 * be read.
 */
function LoopingVideo({ src, poster, className, }) {
    const video = React.useRef(null);
    React.useEffect(() => {
        const stillness = window.matchMedia("(prefers-reduced-motion: reduce)");
        const apply = () => {
            const element = video.current;
            if (!element)
                return;
            if (stillness.matches) {
                element.pause();
                return;
            }
            // A browser may decline to play (a data-saving mode, a policy this clip
            // does not satisfy). There is nothing to recover: the poster stays, which
            // is exactly what a reduced-motion visitor sees.
            void element.play().catch(() => { });
        };
        apply();
        stillness.addEventListener("change", apply);
        return () => stillness.removeEventListener("change", apply);
    }, []);
    return (_jsx("video", { ref: video, "data-block-video": true, src: src, poster: poster, className: className, muted: true, loop: true, "aria-hidden": true, playsInline: true, preload: "metadata" }));
}
/**
 * Renders a resolved image; for an empty slot it either shows the dashed EDITOR
 * placeholder (CMS canvas / component showcase, `placeholders='editor'`) or the
 * block's graceful `emptyRender` fallback (public site / Preview, the default),
 * so the public site never leaks editor chrome.
 *
 * A slot declaring more than one target renders one `<img>` per framing, with
 * only the one made for this screen displayed — see {@link FRAMING_VISIBILITY}.
 */
export function MediaSlotView({ image, className, ratio = "16 / 9", label = "Image", emptyRender, }) {
    const { placeholders } = useBlockRender();
    // A VIDEO IS SERVED AS UPLOADED. There is no rendition ladder and no
    // per-screen framing to choose between, so this branch is deliberately
    // simpler than the picture one below: one file, covered into the slot's box
    // by CSS, with the poster showing until it plays.
    if (image?.url && image.mediaType === "video") {
        // Decoration loops silently and offers nothing to press; content is
        // watched, and nothing about it starts on its own.
        if (image.playback === "loop") {
            return (_jsx(LoopingVideo, { src: image.url, poster: image.poster, className: cn(IMAGE_CLASS, className) }));
        }
        return (_jsx("video", { "data-block-video": true, src: image.url, poster: image.poster, className: cn(IMAGE_CLASS, className), controls: true, playsInline: true, preload: "metadata" }));
    }
    const visibility = image?.sources
        ? FRAMING_VISIBILITY[image.sources.length]
        : undefined;
    if (image?.url && image.sources && visibility) {
        // Tallest framing first, so the narrowest screen — the one no step covers —
        // gets the tall one. Ordering by shape rather than by declaration order
        // keeps this independent of how a component happens to list its targets.
        const framings = [...image.sources].sort((a, b) => a.width / a.height - b.width / b.height);
        return (_jsx(_Fragment, { children: framings.map((framing, index) => (_jsx("img", { src: framing.url, alt: image.alt ?? "", width: framing.width, height: framing.height, className: cn(IMAGE_CLASS, className, visibility[index]) }, framing.url))) }));
    }
    if (image?.url) {
        return (_jsx("img", { src: image.url, alt: image.alt ?? "", width: image.width, height: image.height, className: cn(IMAGE_CLASS, className) }));
    }
    if (placeholders === "editor") {
        return (_jsx("div", { "aria-hidden": "true", className: cn("flex w-full items-center justify-center rounded-lg border border-dashed border-border bg-muted/40 text-sm font-medium text-muted-foreground", className), style: { aspectRatio: ratio }, children: label }));
    }
    return _jsx(_Fragment, { children: emptyRender });
}
//# sourceMappingURL=context.js.map